import { ChangeEvent, useCallback } from 'react';
import { createSyntheticChangeEvent } from '../utils';

interface UseAutoResetSelectionParams<T> {
  fieldName: string;
  resetValue: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onItemsChange: (items: string[]) => void;
  selectedItems: string[];
  allItems: T[];
  getItemId?: (item: T) => string;
}

interface UseAutoResetSelectionReturn {
  handleSubmit: (items: string[]) => void;
  handleRemove: (item: string | number) => void;
  handleClose: () => void;
}

export function useAutoResetSelection<T>({
  fieldName,
  resetValue,
  onChange,
  onItemsChange,
  selectedItems,
  allItems,
  getItemId = (item: T) => (item as unknown as { id: string }).id,
}: UseAutoResetSelectionParams<T>): UseAutoResetSelectionReturn {
  const resetToDefault = useCallback(() => {
    onChange(createSyntheticChangeEvent(fieldName, resetValue));
    onItemsChange([]);
  }, [fieldName, resetValue, onChange, onItemsChange]);

  const handleSubmit = useCallback(
    (items: string[]) => {
      const enabledItemIds = allItems.map(getItemId);
      const enabledIdsSet = new Set(enabledItemIds);
      const allEnabledSelected =
        enabledIdsSet.size > 0 &&
        items.length === enabledIdsSet.size &&
        items.every((id) => enabledIdsSet.has(id));

      if (allEnabledSelected || items.length === 0) {
        resetToDefault();
      } else {
        onItemsChange(items);
      }
    },
    [allItems, getItemId, resetToDefault, onItemsChange],
  );

  const handleRemove = useCallback(
    (item: string | number) => {
      const itemId = item.toString();

      const updatedItems = selectedItems.filter((id) => {
        const isEqual = id.toString() === itemId;
        return !isEqual;
      });

      if (updatedItems.length === 0) {
        resetToDefault();
      } else {
        onItemsChange(updatedItems);
      }
    },
    [selectedItems, resetToDefault, onItemsChange],
  );

  const handleClose = useCallback(() => {
    if (selectedItems.length === 0) {
      resetToDefault();
    }
  }, [selectedItems.length, resetToDefault]);

  return {
    handleSubmit,
    handleRemove,
    handleClose,
  };
}

export default useAutoResetSelection;
